/**
 * What every table column means, one line each, shown from the ? next to its header (Zack,
 * 2026-10-15: "create help text for all columns in the details tables"). The one place these
 * strings live, so a number reads the same in every table. Hidden until asked for, so it is the
 * documented exception to UI standard 6 (as little text as possible). Users see Guidance, never
 * plan (UI standard 11).
 */

/** My Team: the Details and Summary tables (both keyed by `MetricValues`, plus the name). */
export const TEAM_HELP = {
  name: "Your direct report. Open the name for their month in detail.",
  capacity: "Work hours in the month (capacity).",
  pto: "Time off booked this month: vacation, sick, holiday, other.",
  available: "Capacity − PTO. The base for every % on this page.",
  planned: "Guidance hours across all demands this month (vacation guidance excluded).",
  plannedBillable: "Guidance on client demands (not internal or presales).",
  plannedPct: "Total guidance ÷ (capacity − PTO).",
  targetHours: "(Capacity − PTO) × billable target %.",
  targetPct:
    "Billable target set yearly by department leadership. Billable % is judged against it to date.",
  billable: "Billable hours logged so far, through yesterday.",
  billablePct: "Billable logged ÷ (capacity to date − PTO to date).",
  logged: "Productive hours logged so far, through yesterday (excludes PTO).",
  utilization: "Total logged ÷ (capacity to date − PTO to date).",
} as const;

/** My Team Summary columns whose cell shows more than the Details number of the same key. */
export const TEAM_SUMMARY_HELP = {
  plannedPct: "Guidance hours of available (capacity − PTO). Under 75% is tagged Under guidance.",
  billablePct:
    "Billable logged ÷ (capacity − PTO), to date, against the target. Under 90% of target is Below target.",
} as const;

/** Person panel, Summary tab: one row per month. */
export const PERSON_MONTHS_HELP = {
  month: "The calendar month. The month you are viewing is highlighted.",
  available: "Capacity − PTO for the month.",
  targetPct: "Billable target set yearly by department leadership.",
  planned: "Guidance hours across all demands that month.",
  billable: "Billable hours logged, through yesterday for the current month.",
  logged: "Productive hours logged (excludes PTO), through yesterday for the current month.",
  billablePct: "Billable logged ÷ (capacity − PTO), both to date.",
  utilization: "Total logged ÷ (capacity − PTO), both to date.",
  amount: "Dollar value of the billable hours logged, to date.",
} as const;

/** Person panel, Guidance tab: one row per assignment. */
export const PERSON_GUIDANCE_HELP = {
  demand: "The demand the person is assigned to; Billable unless it is internal or presales.",
  role: "The person's role on this demand.",
  rate: "Hourly rate for the person on this demand (the demand's default when none is set).",
  planned: "Guidance hours set for the person on this demand this month.",
  billable: "Billable hours logged on this demand this month.",
} as const;

/** Person panel, Time entries tab: one row per entry. */
export const PERSON_ENTRIES_HELP = {
  date: "The day the hours were worked.",
  task: "The task, with its demand or project.",
  type: "Billable, Non-billable, Internal, Presales or PTO.",
  hours: "Hours on this entry.",
  comment: "The note saved with the entry.",
} as const;

/** Time page, Entries (week and month): one row per entry of your own. */
export const TIME_ENTRIES_HELP = {
  date: "The day the hours were worked.",
  task: "The task and its project; PTO shows as Time off.",
  type: "Billable, Non-billable, Internal, Presales or PTO, from the task and its demand.",
  hours: "Hours on this entry, in quarter hours.",
  comment: "Your note on the entry. Entries of 2 hrs or more need one.",
} as const;

/** Guidance page: Guidance vs logged, one row per assignment. */
export const GUIDANCE_HELP = {
  demand: "The demand you are assigned to, with its account.",
  role: "Your role on this demand.",
  planned: "Guidance: hours set for you on this demand this month.",
  logged: "Productive hours you logged on this demand this month.",
  billable: "Billable hours you logged on this demand this month.",
} as const;

/** Home: the month's time by task. */
export const HOME_MONTH_TIME_HELP = {
  task: "The task, with its project.",
  type: "Billable, Non-billable, Internal, Presales or PTO.",
  hours: "Hours you logged on this task this month, PTO included.",
} as const;

/** PTO requests to decide: My Team's panel and the PTO page's approvals, one row per request. */
export const PTO_REQUESTS_HELP = {
  person: "Who asked for the time off, with their note.",
  type: "The kind of time off: vacation, sick, holiday or another type.",
  start: "First and last day off. Sorts by the first day.",
  days: "Working days off; weekends and holidays already booked are not counted.",
  status: "To approve, HR approves, Approved, Rejected or Cancel asked.",
} as const;

/** PTO page: My requests, one row per request. */
export const MY_REQUESTS_HELP = {
  dates: "First and last day off. Sorts by the first day.",
  type: "The kind of time off: vacation, sick, holiday or another type.",
  days: "Working days off; weekends and holidays already booked are not counted.",
  status: "Draft, Pending, Approved, Rejected or Cancel asked.",
  note: "Your note, or the approver's reason when rejected.",
  approver: "Who decides this request.",
} as const;

/** My Expenses: the expenses table. */
export const EXPENSES_HELP = {
  submitted: "The day you submitted it. Empty on a draft.",
  description: "What the expense is for.",
  type: "The kind of expense. It sets the department and any stipend limit.",
  approval: "Draft, Pending, Approved or Rejected.",
  payment: "Whether Finance has paid it.",
  estPaid: "Set at submit: the end of the month of the submit date plus the payment term.",
  amount: "The sum of the expense's lines.",
} as const;

/** My Expenses: the To approve table. */
export const EXPENSE_APPROVALS_HELP = {
  person: "Whose expense it is.",
  description: "What the expense is for.",
  type: "The kind of expense.",
  department: "The department paying; its owners approve.",
  po: "The purchase order, when the expense is on one.",
  month: "The month the expense belongs to.",
  submitted: "The day it was submitted.",
  amount: "The sum of the expense's lines.",
} as const;

/**
 * My Incentives: the Company and Personal goals tables. Wording from the 2026 Variable
 * Compensation plan (generic plan rules only; DECISIONS 2026-10-15).
 */
export const INCENTIVE_HELP = {
  goal: "The goal you are measured on, with its level. Open ⓘ for what it measures.",
  weight: "Share of your award this goal is worth.",
  range: "Min / target / max for the year. Below min earns 0%.",
  actual: "Year-to-date result.",
  projected: "Result projected to year end.",
  achieved: "Min = 0%, target = 100%, stretch = 200%.",
  awarded: "% achieved × weight. Some goals cap at their weight.",
} as const;

/** Profile: the person's calendar match rules. */
export const CALENDAR_RULES_HELP = {
  type: "What the rule looks at: the title, words in the title, an attendee's email domain, or the meeting series.",
  matches: "The title, words or domain the rule looks for.",
  task: "The task a matching meeting is logged to.",
  source: "Yours: added or edited by you. Learned: from meetings you logged.",
  used: "How many logged meetings this rule picked the task for.",
  lastUsed: "When it last picked the task for a logged meeting.",
} as const;

/** Settings, Jobs: the scheduled jobs. */
export const JOBS_HELP = {
  job: "The scheduled job, in plain words.",
  mode: "Off: never runs. Dry run: works it out, changes nothing. Live: acts. Set by the environment.",
  schedule: "When it runs, in UTC, so it moves an hour against local time when the clocks change.",
  lastRun: "Its newest recorded run and how it ended. Dry runs are logged, not recorded here.",
  lastResult: "What the newest run counted, or why it failed.",
  nextRun: "Its next slot, in UTC. None while the job is off.",
} as const;

/** Settings, Jobs: the run history. */
export const JOB_RUNS_HELP = {
  job: "The job this run was for.",
  slot: "The scheduled time the run is for (UTC), not when it started.",
  status: "Failed is tried again while attempts remain; Gave up means the last attempt failed.",
  attempts: "How many times this slot was tried, at most 3.",
  startedAt: "When the latest attempt started (UTC).",
  finishedAt: "When it ended (UTC). Empty while running.",
  result: "What the run counted, or why it failed.",
} as const;
